<template>
    <section class="icp-page min-h-screen w-full">
        <div class="icp-page-bg" aria-hidden="true"></div>
        <div class="relative container mx-auto px-4 sm:px-8 py-8 sm:py-12">
            <div class="flex flex-col h-full w-full">
                <nav class="icp-breadcrumb text-sm mb-8 flex flex-wrap items-center">
                    <router-link to="/dashboard" class="icp-breadcrumb-link">Dashboard</router-link>
                    <span class="icp-breadcrumb-sep mx-2">/</span>
                    <span class="icp-breadcrumb-current">Internship / Clinical Placement</span>
                </nav>

                <header class="icp-hero mb-10 sm:mb-14">
                    <p class="icp-hero-kicker">Career Development</p>
                    <h1 class="icp-hero-title">Internship / Clinical Placement</h1>
                    <p class="icp-hero-lead">
                        Create internship / clinical placement opportunities and manage them by entity and campus.
                    </p>
                </header>

                <!-- Opportunities list (layout aligned with document-control Document records) -->
                <div class="bg-white shadow-md rounded-3xl flex-auto flex flex-col border border-gray-100">
                    <div class="p-6 border-b border-gray-200">
                        <div class="flex items-center justify-between gap-3 flex-wrap">
                            <div class="min-w-0">
                                <h2 class="text-lg font-semibold text-gray-900">{{ listHeading }}</h2>
                                <p v-if="showScopeTabs" class="text-sm text-gray-500 mt-1">
                                    Switch between system-wide opportunities and those you created.
                                </p>
                            </div>
                            <button v-if="hasPermission('internship-create')" type="button"
                                @click.stop="openCreateModal" class="icl-add-btn" :disabled="isSubmitting">
                                <icon-plus class="icl-add-btn__icon"></icon-plus>
                                <span>Create opportunity</span>
                            </button>
                        </div>

                        <div v-if="showScopeTabs" class="icl-scope-tabs mt-4" role="tablist"
                            aria-label="Filter opportunities by ownership">
                            <button v-for="tab in scopeTabs" :key="tab.value" type="button" role="tab"
                                class="icl-scope-tab" :class="{ 'icl-scope-tab-active': listScope === tab.value }"
                                :aria-selected="listScope === tab.value" @click="selectScope(tab.value)">
                                {{ tab.label }}
                            </button>
                        </div>
                    </div>

                    <div v-if="listLoading" class="flex-auto flex justify-center items-center py-12">
                        <div class="text-center max-w-md px-4">
                            <div class="text-gray-700 font-medium mb-1">Loading…</div>
                            <div class="text-gray-500 text-sm">Fetching internship opportunities.</div>
                        </div>
                    </div>

                    <div v-else-if="!hasItems" class="flex-auto flex justify-center items-center py-12">
                        <div class="text-center max-w-md px-4">
                            <h3 class="text-xl font-semibold text-gray-800 mb-2">No opportunities yet</h3>
                            <p class="text-gray-500 mb-4">
                                <template v-if="listScope === 'all' && canViewAllInternship">
                                    No internship opportunities have been created in the system yet.
                                </template>
                                <template v-else>
                                    Create an opportunity to list it here by entity and campus.
                                </template>
                            </p>
                            <button v-if="hasPermission('internship-create') && listScope !== 'all'" type="button"
                                @click.stop="openCreateModal" class="icl-add-btn" :disabled="isSubmitting">
                                <icon-plus class="icl-add-btn__icon"></icon-plus>
                                <span>Create opportunity</span>
                            </button>
                        </div>
                    </div>

                    <div v-else class="flex flex-col flex-auto">
                        <div class="flex-auto flex flex-col overflow-x-auto">
                            <div class="inline-block min-w-full py-2 h-full">
                                <div class="overflow-x-auto h-full">
                                    <table class="min-w-full text-left text-sm font-light">
                                        <thead class="border-b font-medium dark:border-neutral-500">
                                            <tr>
                                                <th scope="col" class="px-6 py-2 text-gray-400 w-8">#</th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">Type</th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">Title</th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">Entity</th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">Campus</th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">Capacity</th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">Enrollments</th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">Created</th>
                                                <th v-if="showCreatedByColumn" scope="col"
                                                    class="px-6 py-2 text-gray-400">
                                                    Created by
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">Status</th>
                                                <th scope="col" class="px-6 py-2 text-gray-400 w-28 text-right">Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(item, index) in items" :key="item.id"
                                                class="border-b border-dashed dark:border-neutral-500 hover:bg-gray-50">
                                                <td class="whitespace-nowrap px-6 py-2 font-medium text-gray-900">
                                                    {{ paginatorInfo.firstItem + index }}
                                                </td>
                                                <td class="whitespace-nowrap px-6 py-2 text-gray-900">
                                                    {{ formatOpportunityType(item.opportunity_type) }}
                                                </td>
                                                <td class="px-6 py-2 text-gray-900">
                                                    <router-link v-if="item.token && item.title"
                                                        :to="'/internship-clinical-placement/' + item.token"
                                                        target="_blank"
                                                        class="icl-title-link max-w-xs truncate font-medium block"
                                                        :title="item.title">
                                                        {{ item.title }}
                                                    </router-link>
                                                    <span v-else
                                                        class="max-w-xs truncate font-medium text-gray-900 block">
                                                        {{ item.title ? item.title : '—' }}
                                                    </span>
                                                </td>
                                                <td class="whitespace-nowrap px-6 py-2 text-gray-900">
                                                    {{ formatEntityName(item.entity) }}
                                                </td>
                                                <td class="px-6 py-2 text-gray-900">
                                                    <div class="max-w-xs truncate"
                                                        :title="formatCampuses(item.campus_code)">
                                                        {{ formatCampuses(item.campus_code) || '—' }}
                                                    </div>
                                                </td>
                                                <td class="whitespace-nowrap px-6 py-2">
                                                    <span v-if="item.student_count === null"
                                                        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-800">
                                                        Unlimited
                                                    </span>
                                                    <span v-else
                                                        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
                                                        {{ item.student_count }}
                                                    </span>
                                                </td>
                                                <td class="px-6 py-2">
                                                    <div class="icl-enrollment-summary">
                                                        <button type="button" class="icl-enrollment-total"
                                                            :aria-label="enrollmentBreakdownTitle(item)"
                                                            @mouseenter="showEnrollmentTooltip($event, item)"
                                                            @mouseleave="hideEnrollmentTooltip"
                                                            @focus="showEnrollmentTooltip($event, item)"
                                                            @blur="hideEnrollmentTooltip">
                                                            <span class="icl-enrollment-total-value">
                                                                {{ formatEnrolledCount(item) }}
                                                            </span>
                                                            <svg class="icl-enrollment-info-icon" viewBox="0 0 20 20"
                                                                fill="currentColor" aria-hidden="true">
                                                                <path fill-rule="evenodd"
                                                                    d="M18 10A8 8 0 11 2 10a8 8 0 0116 0zm-7-3a1 1 0 10-2 0 1 1 0 002 0zm-1 2a1 1 0 00-1 1v3a1 1 0 102 0v-3a1 1 0 00-1-1z"
                                                                    clip-rule="evenodd" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </td>
                                                <td class="whitespace-nowrap px-6 py-2 text-gray-900">
                                                    {{ formatDateTime(item.created_at) || '—' }}
                                                </td>
                                                <td v-if="showCreatedByColumn"
                                                    class="whitespace-nowrap px-6 py-2 text-gray-900">
                                                    <span class="max-w-[10rem] truncate block"
                                                        :title="formatCreatedBy(item)">
                                                        {{ formatCreatedBy(item) }}
                                                    </span>
                                                </td>
                                                <td class="whitespace-nowrap px-6 py-2">
                                                    <span v-if="isOpportunityActive(item)"
                                                        class="icl-status-chip icl-status-chip-active">
                                                        Active
                                                    </span>
                                                    <span v-else class="icl-status-chip icl-status-chip-inactive">
                                                        Inactive
                                                    </span>
                                                </td>
                                                <td class="px-6 py-2">
                                                    <div class="flex justify-end gap-2">
                                                        <button type="button" title="View"
                                                            class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group"
                                                            @click.stop="openViewModal(item)">
                                                            <icon-eye
                                                                class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-eye>
                                                        </button>
                                                        <button v-if="canEditOpportunity(item)" type="button"
                                                            title="Edit"
                                                            class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group"
                                                            @click.stop="openEditModal(item)">
                                                            <icon-pen
                                                                class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-pen>
                                                        </button>
                                                        <button v-if="canDeleteOpportunity(item)" type="button"
                                                            title="Delete"
                                                            class="bg-gray-100 hover:bg-red-50 w-8 h-8 flex justify-center items-center rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                                            :disabled="deletingOpportunityId === item.id"
                                                            @click.stop="confirmDeleteOpportunity(item)">
                                                            <icon-trash class="w-4 h-4"></icon-trash>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div class="mt-4 px-6 pb-6" v-if="paginatorInfo && paginatorInfo.total > 0">
                            <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
                        </div>
                    </div>
                </div>

                <base-full-modal v-if="isViewModalOpen" :title="viewModalTitle" height="85vh" width="900px"
                    @close="closeViewModal">
                    <internship-opportunity-detail v-if="viewOpportunityToken" :token="viewOpportunityToken"
                        :embedded="true"></internship-opportunity-detail>
                </base-full-modal>

                <base-modal v-if="isFormModalOpen" :title="formModalTitle" @close="closeFormModal" width="800px">
                    <div class="px-6 pt-5 pb-6">
                        <div
                            class="mb-5 rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-blue-50 p-4">
                            <div class="flex items-start gap-3">
                                <div
                                    class="h-10 w-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow">
                                    <span class="text-lg font-bold">{{ isEditMode ? '✎' : '+' }}</span>
                                </div>
                                <div class="min-w-0">
                                    <div class="text-base font-semibold text-gray-900">{{ formModalHeading }}</div>
                                    <div class="text-sm text-gray-600 leading-relaxed">
                                        {{ formModalSubheading }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <form @submit.prevent="submitForm" class="space-y-5">
                            <div class="rounded-2xl border border-gray-200 bg-white p-4">
                                <div class="text-sm font-semibold text-gray-900 mb-3">Scope</div>

                                <div class="flex flex-col mb-4">
                                    <label class="block text-sm font-medium text-gray-700 mb-2">
                                        Opportunity type <span class="text-red-600">*</span>
                                    </label>
                                    <div class="flex flex-col sm:flex-row sm:flex-wrap gap-3">
                                        <label
                                            class="inline-flex items-center gap-2 cursor-pointer rounded-lg border px-4 py-2 text-sm transition"
                                            :class="form.opportunity_type === 'clinical_placement'
                                                ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                                                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'">
                                            <input v-model="form.opportunity_type" type="radio"
                                                value="clinical_placement" class="text-indigo-600 focus:ring-indigo-500"
                                                @change="clearValidation('opportunity_type')" />
                                            Clinical Placement
                                        </label>
                                        <label
                                            class="inline-flex items-center gap-2 cursor-pointer rounded-lg border px-4 py-2 text-sm transition"
                                            :class="form.opportunity_type === 'internship'
                                                ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                                                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'">
                                            <input v-model="form.opportunity_type" type="radio" value="internship"
                                                class="text-indigo-600 focus:ring-indigo-500"
                                                @change="clearValidation('opportunity_type')" />
                                            Internship
                                        </label>
                                    </div>
                                    <p v-if="validations.opportunity_type" class="text-xs text-red-600 mt-1">
                                        {{ validations.opportunity_type }}</p>
                                </div>

                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <!-- Entity -->
                                    <div class="flex flex-col">
                                        <label class="block text-sm font-medium text-gray-700 mb-2">
                                            Entity <span class="text-red-600">*</span>
                                        </label>

                                        <multiselect v-model="selectedEntityOption" :options="entityMultiselectOptions"
                                            :multiple="false" :close-on-select="true" :preserve-search="true"
                                            placeholder="Select entity..." label="label" track-by="value"
                                            :disabled="isSubmitting"
                                            :class="validations.entity ? 'multiselect-invalid' : ''"
                                            @select="onEntitySelected" @remove="onEntityRemoved">
                                        </multiselect>

                                        <p v-if="validations.entity" class="text-xs text-red-600 mt-1">{{
                                            validations.entity }}</p>
                                    </div>

                                    <!-- Campus -->
                                    <div class="flex flex-col">
                                        <label class="block text-sm font-medium text-gray-700 mb-2">
                                            Campus <span class="text-red-600">*</span>
                                        </label>

                                        <multiselect v-model="form.campus_code" :options="campusOptions"
                                            :multiple="true" :close-on-select="false" :preserve-search="true"
                                            placeholder="Select campus..." label="label" track-by="value"
                                            :disabled="campusesLoading || !form.entity"
                                            :class="validations.campus_code ? 'multiselect-invalid' : ''"
                                            @input="clearValidation('campus_code')">
                                        </multiselect>

                                        <div class="mt-1 flex items-center justify-between gap-3">
                                            <p v-if="campusesLoading" class="text-xs text-gray-500">Loading campuses…
                                            </p>
                                            <p v-else class="text-xs text-gray-500">You can select multiple campuses.
                                            </p>
                                        </div>
                                        <p v-if="validations.campus_code" class="text-xs text-red-600 mt-1">
                                            {{ validations.campus_code }}
                                        </p>
                                    </div>
                                </div>

                                <!-- Staff student selection (UI-only toggle; not persisted) -->
                                <div class="mt-4">
                                    <div class="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                                        <p class="m-0 mb-3 text-sm font-medium text-gray-800">
                                            Do you want to select students for this opportunity?
                                        </p>
                                        <div class="flex flex-wrap items-center gap-4">
                                            <label class="inline-flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                                                <input v-model="wantSelectStudents" type="radio" :value="false"
                                                    class="text-blue-600 focus:ring-blue-500"
                                                    @change="onWantSelectStudentsChange" />
                                                No
                                            </label>
                                            <label class="inline-flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                                                <input v-model="wantSelectStudents" type="radio" :value="true"
                                                    class="text-blue-600 focus:ring-blue-500"
                                                    @change="onWantSelectStudentsChange" />
                                                Yes
                                            </label>
                                        </div>
                                    </div>

                                    <div v-if="wantSelectStudents" class="mt-4">
                                        <label for="icl-student-picker-search"
                                            class="block mb-2 text-sm font-medium text-gray-700">
                                            Select students
                                        </label>
                                        <p class="m-0 mb-3 text-xs leading-relaxed text-gray-500">
                                            Active students for the selected entity and campuses (registered and not registered).
                                            <template v-if="isEditMode">
                                                Students who already requested enrollment themselves are hidden here to avoid duplicates.
                                                Unchecking a student removes their staff assignment.
                                            </template>
                                        </p>

                                        <div v-if="!form.entity || !form.campus_code || form.campus_code.length === 0"
                                            class="px-4 py-6 text-center bg-gray-50 border border-dashed border-gray-200 rounded-2xl">
                                            <p class="m-0 text-sm text-gray-500">
                                                Select an entity and at least one campus to load students.
                                            </p>
                                        </div>

                                        <div v-else-if="studentsLoading" class="py-8 text-center">
                                            <p class="m-0 text-sm text-gray-500">Loading active students…</p>
                                        </div>

                                        <div v-else-if="!activeStudents.length"
                                            class="px-4 py-8 text-center bg-white border border-gray-200 rounded-2xl">
                                            <h4 class="m-0 mb-2 text-sm font-semibold text-gray-900">No active students found</h4>
                                            <p class="m-0 text-xs text-gray-500">
                                                There are no active students for the selected entity and campuses.
                                            </p>
                                        </div>

                                        <div v-else class="space-y-3">
                                            <input id="icl-student-picker-search" v-model="studentPickerSearch" type="search"
                                                placeholder="Search by name, student ID, or Emirates ID"
                                                class="w-full px-3 py-2 text-sm text-gray-800 bg-white border border-gray-200 rounded-xl shadow-sm outline-none focus:border-blue-400" />

                                            <div class="icl-student-picker bg-white border border-gray-200 rounded-2xl overflow-hidden">
                                                <div v-if="!filteredActiveStudents.length" class="px-4 py-8 text-center">
                                                    <p class="m-0 text-sm text-gray-500">No students match your search.</p>
                                                </div>
                                                <ul v-else class="icl-student-picker-list m-0 p-0 list-none">
                                                    <li v-for="student in filteredActiveStudents" :key="student.student_id"
                                                        class="icl-student-picker-item">
                                                        <label class="icl-student-picker-label flex items-start gap-3 px-4 py-3 cursor-pointer">
                                                            <input type="checkbox"
                                                                class="icl-student-picker-checkbox mt-0.5 flex-shrink-0"
                                                                :checked="isStudentSelected(student.student_id)"
                                                                @change="toggleStudentSelection(student.student_id, $event)" />
                                                            <span class="min-w-0 text-left">
                                                                <span class="flex flex-wrap items-center gap-2">
                                                                    <span class="text-sm font-semibold text-gray-900 break-words">
                                                                        {{ student.name_en || '—' }}
                                                                    </span>
                                                                    <span class="icl-student-reg-pill"
                                                                        :class="student.is_registered ? 'icl-student-reg-pill--yes' : 'icl-student-reg-pill--no'">
                                                                        {{ student.is_registered ? 'Registered' : 'Not registered' }}
                                                                    </span>
                                                                </span>
                                                                <span class="block mt-0.5 text-xs leading-relaxed text-gray-500">
                                                                    Student ID: {{ student.student_id || '—' }}
                                                                </span>
                                                                <span class="block mt-0.5 text-xs leading-relaxed text-gray-400">
                                                                    Emirates ID: {{ student.eid || '—' }}
                                                                    <template v-if="student.campus_code">
                                                                        · {{ student.campus_code }}
                                                                    </template>
                                                                </span>
                                                            </span>
                                                        </label>
                                                    </li>
                                                </ul>
                                            </div>

                                            <div
                                                class="flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-xl border border-blue-100 bg-blue-50">
                                                <p class="m-0 text-sm text-blue-900">
                                                    <span class="font-semibold">{{ selectedStudentIds.length }}</span>
                                                    of
                                                    <span class="font-semibold">{{ activeStudents.length }}</span>
                                                    student{{ activeStudents.length === 1 ? '' : 's' }} selected
                                                </p>
                                                <div class="flex items-center gap-3">
                                                    <button v-if="!allFilteredStudentsSelected" type="button"
                                                        class="text-xs font-semibold text-blue-800 bg-transparent border-0 cursor-pointer hover:underline p-0"
                                                        @click="selectAllFilteredStudents">
                                                        Select all
                                                    </button>
                                                    <button v-if="selectedStudentIds.length" type="button"
                                                        class="text-xs font-semibold text-blue-800 bg-transparent border-0 cursor-pointer hover:underline p-0"
                                                        @click="clearStudentSelection">
                                                        Clear selection
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="rounded-2xl border border-gray-200 bg-white p-4">
                                <div class="text-sm font-semibold text-gray-900 mb-3">Details</div>

                                <!-- Title -->
                                <div class="flex flex-col">
                                    <label class="block text-sm font-medium text-gray-700 mb-2">
                                        Title <span class="text-red-600">*</span>
                                    </label>
                                    <input v-model="form.title" type="text"
                                        :class="validations.title ? 'ftv-input ftv-input-invalid' : 'ftv-input'"
                                        placeholder="e.g. Clinical rotation – Internal Medicine"
                                        @input="clearValidation('title')" />
                                    <p v-if="validations.title" class="text-xs text-red-600 mt-1">{{ validations.title
                                        }}</p>
                                </div>

                                <!-- Description -->
                                <div class="flex flex-col mt-4">
                                    <label class="block text-sm font-medium text-gray-700 mb-2">Description</label>
                                    <textarea v-model="form.description" rows="4" class="ftv-textarea"
                                        placeholder="Optional: add a short overview, expectations, or requirements."></textarea>
                                </div>

                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                    <!-- Student count -->
                                    <div class="flex flex-col">
                                        <label class="block text-sm font-medium text-gray-700 mb-2">
                                            Student count
                                            <span class="text-xs text-gray-500 font-normal">(blank = unlimited)</span>
                                        </label>
                                        <input v-model="form.student_count" type="number" min="1"
                                            :class="validations.student_count ? 'ftv-input ftv-input-invalid' : 'ftv-input'"
                                            placeholder="e.g. 25" @input="clearValidation('student_count')" />
                                        <p v-if="hasSelectedStudents" class="text-xs text-gray-500 mt-1">
                                            Must be blank (unlimited) or at least the number of selected students
                                            ({{ selectedStudentIds.length }}).
                                        </p>
                                        <p v-if="validations.student_count" class="text-xs text-red-600 mt-1">
                                            {{ validations.student_count }}
                                        </p>
                                    </div>

                                    <!-- Listing window -->
                                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div class="flex flex-col">
                                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                                Listing opens <span class="text-red-600">*</span>
                                            </label>
                                            <input v-model="form.start_date" type="date"
                                                :class="validations.start_date ? 'ftv-input ftv-input-invalid' : 'ftv-input'"
                                                @input="clearValidation('start_date')" />
                                            <p v-if="validations.start_date" class="text-xs text-red-600 mt-1">
                                                {{ validations.start_date }}
                                            </p>
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                                Listing closes <span class="text-red-600">*</span>
                                            </label>
                                            <input v-model="form.end_date" type="date"
                                                :class="validations.end_date ? 'ftv-input ftv-input-invalid' : 'ftv-input'"
                                                @input="clearValidation('end_date')" />
                                            <p v-if="validations.end_date" class="text-xs text-red-600 mt-1">
                                                {{ validations.end_date }}
                                            </p>
                                        </div>
                                    </div>
                                    <p class="m-0 mt-2 text-xs leading-relaxed text-gray-500">
                                        Period when this opportunity is visible to students for enrollment.
                                    </p>
                                </div>
                            </div>

                            <div class="flex items-center justify-end gap-3 pt-1">
                                <button type="button" @click="closeFormModal"
                                    class="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                    :disabled="isSubmitting">
                                    Cancel
                                </button>
                                <button type="submit"
                                    class="px-5 py-2 bg-blue-600 text-white rounded-lg shadow-sm hover:bg-blue-700 transition disabled:opacity-60 disabled:cursor-not-allowed"
                                    :disabled="isSubmitting || campusesLoading">
                                    <span v-if="isSubmitting">{{ isEditMode ? 'Saving…' : 'Creating…' }}</span>
                                    <span v-else>{{ isEditMode ? 'Save changes' : 'Create' }}</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </base-modal>
            </div>
        </div>

        <teleport to="body">
            <div v-if="enrollmentTooltip.visible" class="icl-enrollment-tooltip"
                :class="'icl-enrollment-tooltip-' + enrollmentTooltip.placement"
                :style="{ left: enrollmentTooltip.left + 'px', top: enrollmentTooltip.top + 'px' }" role="tooltip">
                <div class="icl-enrollment-tooltip-title">Enrollment status</div>
                <div class="icl-enrollment-tooltip-row">
                    <span class="icl-enrollment-tooltip-label">
                        <span class="icl-enrollment-tooltip-dot icl-enrollment-tooltip-dot-pending"></span>
                        Pending
                    </span>
                    <strong>{{ enrollmentTooltip.pending }}</strong>
                </div>
                <div class="icl-enrollment-tooltip-row">
                    <span class="icl-enrollment-tooltip-label">
                        <span class="icl-enrollment-tooltip-dot icl-enrollment-tooltip-dot-approved"></span>
                        Approved
                    </span>
                    <strong>{{ enrollmentTooltip.approved }}</strong>
                </div>
                <div class="icl-enrollment-tooltip-row">
                    <span class="icl-enrollment-tooltip-label">
                        <span class="icl-enrollment-tooltip-dot icl-enrollment-tooltip-dot-rejected"></span>
                        Rejected
                    </span>
                    <strong>{{ enrollmentTooltip.rejected }}</strong>
                </div>
            </div>
        </teleport>
    </section>
</template>

<script>
import gql from "graphql-tag";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.css";
import Pagination from "../../components/shared/pagination.vue";
import IconPlus from "../../components/icons/IconPlus.vue";
import IconEye from "../../components/icons/IconEye.vue";
import IconPen from "../../components/icons/IconPen.vue";
import IconTrash from "../../components/icons/IconTrash.vue";
import InternshipOpportunityDetail from "./OpportunityDetail.vue";
import { formatEntityName, formatOpportunityType, getEntityName } from "../../helpers/utils";

export default {
    name: "internship-clinical-placement-other-roles-view",
    components: {
        Multiselect,
        Pagination,
        IconPlus,
        IconEye,
        IconPen,
        IconTrash,
        InternshipOpportunityDetail,
    },
    data() {
        return {
            formModalMode: "",
            editingOpportunityId: null,
            suppressEntityCampusReset: false,
            isViewModalOpen: false,
            viewOpportunityToken: "",
            viewOpportunityTitle: "",
            isSubmitting: false,
            listLoading: false,
            opportunities: null,
            first: 10,
            page: 1,
            campusesLoading: false,
            campusOptions: [],
            selectedEntityOption: null,
            studentsLoading: false,
            activeStudents: [],
            selectedStudentIds: [],
            studentPickerSearch: '',
            studentPickerLoaded: false,
            assignedStudentsLoadFailed: false,
            studentFetchToken: 0,
            /** UI-only: whether staff wants to pick students (not persisted). */
            wantSelectStudents: false,
            /** Edit only: opportunity already had staff-assigned students when opened. */
            hadStaffAssignedOnOpen: false,
            deletingOpportunityId: null,
            listScope: 'mine',
            enrollmentTooltip: {
                visible: false,
                left: 0,
                top: 0,
                placement: 'bottom',
                pending: 0,
                approved: 0,
                rejected: 0,
            },
            form: {
                entity: '',
                opportunity_type: 'clinical_placement',
                campus_code: [],
                title: '',
                description: '',
                student_count: '',
                start_date: '',
                end_date: '',
            },
            validations: {
                entity: '',
                opportunity_type: '',
                campus_code: '',
                title: '',
                student_count: '',
                start_date: '',
                end_date: '',
            },
        };
    },
    computed: {
        entityOptions() {
            const raw = this.$store.getters.entity || '';
            if (!raw) return [];
            return raw.split(",").map((s) => (s || '').trim()).filter((s) => !!s && s !== 'IAT');
        },
        entityMultiselectOptions() {
            var self = this;
            return this.entityOptions.map(function (e) {
                return { label: self.getEntityName(e), value: e };
            });
        },
        items() {
            return this.opportunities && this.opportunities.data ? this.opportunities.data : [];
        },
        hasItems() {
            return this.items && this.items.length > 0;
        },
        paginatorInfo() {
            return this.opportunities && this.opportunities.paginatorInfo ? this.opportunities.paginatorInfo : null;
        },
        viewModalTitle() {
            const title = this.viewOpportunityTitle && this.viewOpportunityTitle.trim()
                ? this.viewOpportunityTitle.trim()
                : "";
            if (title) {
                return "Opportunity details — " + title;
            }
            return "Opportunity details";
        },
        isFormModalOpen() {
            return this.formModalMode === "create" || this.formModalMode === "edit";
        },
        isEditMode() {
            return this.formModalMode === "edit";
        },
        hasSelectedStudents() {
            return this.selectedStudentIds && this.selectedStudentIds.length > 0;
        },
        filteredActiveStudents() {
            const query = (this.studentPickerSearch || '').trim().toLowerCase();
            if (!query) {
                return this.activeStudents;
            }
            return this.activeStudents.filter(function (student) {
                const name = student && student.name_en ? String(student.name_en).toLowerCase() : '';
                const studentId = student && student.student_id ? String(student.student_id).toLowerCase() : '';
                const eid = student && student.eid ? String(student.eid).toLowerCase() : '';
                return name.indexOf(query) !== -1
                    || studentId.indexOf(query) !== -1
                    || eid.indexOf(query) !== -1;
            });
        },
        allFilteredStudentsSelected() {
            if (!this.filteredActiveStudents.length) {
                return false;
            }
            const selectedLookup = {};
            for (let i = 0; i < this.selectedStudentIds.length; i++) {
                selectedLookup[String(this.selectedStudentIds[i])] = true;
            }
            for (let j = 0; j < this.filteredActiveStudents.length; j++) {
                const id = this.filteredActiveStudents[j] && this.filteredActiveStudents[j].student_id
                    ? String(this.filteredActiveStudents[j].student_id)
                    : '';
                if (!id || !selectedLookup[id]) {
                    return false;
                }
            }
            return true;
        },
        formModalTitle() {
            if (this.isEditMode) {
                return "Edit Opportunity";
            }
            return "Create Opportunity";
        },
        formModalHeading() {
            if (this.isEditMode) {
                return "Edit opportunity";
            }
            return "New opportunity";
        },
        formModalSubheading() {
            if (this.isEditMode) {
                return "Update scope, details, and the dates when this opportunity is listed for students.";
            }
            return "Select an entity and one or more campuses, then provide the basic details and listing dates.";
        },
        canViewAllInternship() {
            return this.hasPermission('view-all-internship');
        },
        canCreateInternship() {
            return this.hasPermission('internship-create');
        },
        showScopeTabs() {
            return this.canViewAllInternship && this.canCreateInternship;
        },
        scopeTabs() {
            const tabs = [
                { label: 'All opportunities', value: 'all' },
            ];
            if (this.canCreateInternship) {
                tabs.push({ label: 'My opportunities', value: 'mine' });
            }
            return tabs;
        },
        showCreatedByColumn() {
            return this.canViewAllInternship && this.listScope === 'all';
        },
        listHeading() {
            if (this.canViewAllInternship && this.listScope === 'all') {
                return 'All opportunities';
            }
            return 'My opportunities';
        },
    },
    mounted() {
        if (this.canViewAllInternship) {
            this.listScope = 'all';
        } else if (!this.canCreateInternship) {
            this.listScope = 'mine';
        }
        this.initializeDefaults();
        this.ensureIndexPermissionAndFetch();
    },
    watch: {
        selectedEntityOption: function (newVal, oldVal) {
            const next = newVal && newVal.value ? String(newVal.value) : '';
            const prev = oldVal && oldVal.value ? String(oldVal.value) : '';
            if (next === prev) return;

            this.form.entity = next;
            if (this.suppressEntityCampusReset) {
                return;
            }
            this.form.campus_code = [];
            this.campusOptions = [];
            this.clearStudentPickerState({ keepWantSelect: true });
            this.clearValidation('entity');

            if (next) {
                this.fetchCampusesForEntity(next);
                if (this.wantSelectStudents) {
                    this.fetchActiveStudents();
                }
            }
        },
        'form.campus_code': {
            deep: true,
            handler: function () {
                if (this.suppressEntityCampusReset) {
                    return;
                }
                if (!this.wantSelectStudents) {
                    return;
                }
                this.fetchActiveStudents();
            },
        },
    },
    methods: {
        formatEntityName,
        formatOpportunityType,
        getEntityName,
        openViewModal(item) {
            const t = item && item.token ? String(item.token) : "";
            if (!t) {
                this.toast("error", "This opportunity cannot be opened.");
                return;
            }
            this.viewOpportunityToken = t;
            this.viewOpportunityTitle = item && item.title ? String(item.title) : "";
            this.isViewModalOpen = true;
        },
        closeViewModal() {
            this.isViewModalOpen = false;
            this.viewOpportunityToken = "";
            this.viewOpportunityTitle = "";
        },
        toast(icon, title) {
            if (!this.$swal) {
                return;
            }
            const Toast = this.$swal.mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 1500,
                timerProgressBar: true,
                didOpen: (toast) => {
                    toast.addEventListener("mouseenter", this.$swal.stopTimer);
                    toast.addEventListener("mouseleave", this.$swal.resumeTimer);
                },
            });
            Toast.fire({ icon: icon, title: title });
        },
        hasPermission(crudName) {
            const permissions = this.$store.getters.permissions || '';
            const permissionsArray = permissions.split(",");
            return permissionsArray.includes(crudName);
        },
        isOpportunityOwner(item) {
            if (!item || item.created_by_user_id === null || typeof item.created_by_user_id === 'undefined') {
                return false;
            }
            const uid = this.$store.getters.userId;
            if (uid === null || uid === '' || typeof uid === 'undefined') {
                return false;
            }
            return String(item.created_by_user_id) === String(uid);
        },
        canEditOpportunity(item) {
            if (!this.hasPermission('internship-edit')) {
                return false;
            }
            return this.isOpportunityOwner(item);
        },
        canDeleteOpportunity(item) {
            if (!this.hasPermission('internship-delete')) {
                return false;
            }
            return this.isOpportunityOwner(item);
        },
        async confirmDeleteOpportunity(item) {
            if (!this.canDeleteOpportunity(item)) {
                return;
            }
            var titleText = '';
            if (item && item.title) {
                titleText = item.title;
            }
            var body = 'Remove this opportunity from the list? It can be restored from the database if needed.';
            if (titleText) {
                body = '"' + titleText + '" will be removed from the list. It can be restored from the database if needed.';
            }
            var confirmed = false;
            if (this.$swal && typeof this.$swal.fire === 'function') {
                const res = await this.$swal.fire({
                    icon: 'warning',
                    title: 'Delete opportunity?',
                    text: body,
                    showCancelButton: true,
                    confirmButtonText: 'Delete',
                    cancelButtonText: 'Cancel',
                });
                confirmed = res && res.isConfirmed;
            } else {
                confirmed = window.confirm(body);
            }
            if (!confirmed) {
                return;
            }

            await this.deleteOpportunity(item.id);
        },
        async deleteOpportunity(id) {
            if (!this.hasPermission('internship-delete')) {
                this.$router.replace('/no-permission');
                return;
            }
            var rawId = id;
            if (rawId === null || rawId === '' || typeof rawId === 'undefined') {
                return;
            }
            this.deletingOpportunityId = rawId;
            try {
                const mutation = gql`
                    mutation DeleteInternshipClinicalPlacementOpportunity($id: ID!) {
                        deleteInternshipClinicalPlacementOpportunity(id: $id) {
                            error
                            error_code
                            message
                        }
                    }
                `;
                const result = await this.$apollo.mutate({
                    mutation,
                    variables: { id: String(rawId) },
                });
                const payload = result && result.data && result.data.deleteInternshipClinicalPlacementOpportunity;
                if (!payload || payload.error) {
                    const msg = payload && payload.message ? payload.message : 'Failed to delete.';
                    this.toast('error', msg);
                    return;
                }
                this.toast('success', payload.message || 'Deleted.');
                this.fetchOpportunities();
            } catch (e) {
                console.error('Delete opportunity failed:', e);
                this.toast('error', 'Failed to delete. Please try again.');
            } finally {
                this.deletingOpportunityId = null;
            }
        },
        ensureIndexPermissionAndFetch() {
            if (!this.hasPermission('internship-index')) {
                this.$router.replace('/no-permission');
                return;
            }
            this.fetchOpportunities();
        },
        setPage(page) {
            this.page = page;
            this.fetchOpportunities();
        },
        resolveListScope() {
            if (this.canViewAllInternship) {
                if (this.listScope === 'mine' && this.canCreateInternship) {
                    return 'mine';
                }
                return 'all';
            }
            return 'mine';
        },
        selectScope(scope) {
            if (!this.showScopeTabs) {
                return;
            }
            if (scope !== 'all' && scope !== 'mine') {
                return;
            }
            if (scope === 'mine' && !this.canCreateInternship) {
                return;
            }
            if (scope === this.listScope || this.listLoading) {
                return;
            }
            this.listScope = scope;
            this.page = 1;
            this.fetchOpportunities();
        },
        formatCreatedBy(item) {
            const user = item && item.created_by_user ? item.created_by_user : null;
            if (!user || !user.name) {
                return 'Unknown';
            }
            const name = String(user.name).trim();
            return name ? name : 'Unknown';
        },
        formatDateTime(value) {
            if (!value) return '';
            // Lighthouse DateTime scalar typically returns an ISO string.
            return String(value).replace('T', ' ').replace('Z', '');
        },
        toDateKey(value) {
            if (!value) {
                return '';
            }
            const s = String(value).trim();
            if (s.length >= 10) {
                return s.substring(0, 10);
            }
            return s;
        },
        getTodayDateKey() {
            const d = new Date();
            const y = d.getFullYear();
            const m = d.getMonth() + 1;
            const day = d.getDate();
            const mm = m < 10 ? '0' + m : String(m);
            const dd = day < 10 ? '0' + day : String(day);
            return String(y) + '-' + mm + '-' + dd;
        },
        isOpportunityActive(item) {
            if (!item) {
                return false;
            }
            const start = this.toDateKey(item.start_date);
            const end = this.toDateKey(item.end_date);
            if (!start || !end) {
                return false;
            }
            const today = this.getTodayDateKey();
            return today >= start && today <= end;
        },
        async fetchOpportunities() {
            this.listLoading = true;
            try {
                const scope = this.resolveListScope();
                const result = await this.$apollo.query({
                    query: gql`
                        query InternshipClinicalPlacementOpportunities(
                            $first: Int!
                            $page: Int!
                            $scope: String
                        ) {
                            internshipClinicalPlacementOpportunities(
                                first: $first
                                page: $page
                                scope: $scope
                            ) {
                                error
                                error_code
                                message
                                paginatorInfo {
                                    count
                                    currentPage
                                    firstItem
                                    hasMorePages
                                    lastItem
                                    lastPage
                                    perPage
                                    total
                                }
                                data {
                                    id
                                    token
                                    created_by_user_id
                                    opportunity_type
                                    title
                                    entity
                                    campus_code
                                    description
                                    student_count
                                    enrolled_count
                                    pending_enrollment_count
                                    approved_enrollment_count
                                    rejected_enrollment_count
                                    start_date
                                    end_date
                                    created_at
                                    created_by_user {
                                        id
                                        name
                                    }
                                }
                            }
                        }
                    `,
                    variables: {
                        first: this.first,
                        page: this.page,
                        scope: scope,
                    },
                    fetchPolicy: 'network-only',
                });

                const payload = result && result.data && result.data.internshipClinicalPlacementOpportunities;
                if (!payload || payload.error) {
                    if (payload && payload.error_code === 403) {
                        this.$router.replace('/no-permission');
                        return;
                    }
                    if (payload && payload.error_code === 401) {
                        this.$router.replace('/');
                        return;
                    }
                    const msg = payload && payload.message ? payload.message : 'Failed to load opportunities.';
                    this.toast('error', msg);
                    this.opportunities = { data: [], paginatorInfo: { firstItem: 0, lastPage: 1 } };
                    return;
                }

                this.opportunities = payload;
            } catch (e) {
                console.error('Fetch opportunities failed:', e);
                this.toast('error', 'Failed to load opportunities. Please try again.');
            } finally {
                this.listLoading = false;
            }
        },
        initializeDefaults() {
            if (this.entityOptions.length === 1) {
                this.form.entity = this.entityOptions[0];
                this.selectedEntityOption = { label: this.getEntityName(this.form.entity), value: this.form.entity };
                this.fetchCampusesForEntity(this.form.entity);
            }
        },
        normalizeDateForInput(value) {
            if (!value) {
                return "";
            }
            const s = String(value).trim();
            if (s.length >= 10) {
                return s.substring(0, 10);
            }
            return s;
        },
        resetForm() {
            this.selectedEntityOption = null;
            this.campusOptions = [];
            this.editingOpportunityId = null;
            this.clearStudentPickerState();
            this.form = {
                entity: '',
                opportunity_type: 'clinical_placement',
                campus_code: [],
                title: '',
                description: '',
                student_count: '',
                start_date: '',
                end_date: '',
            };
        },
        clearStudentPickerState(options) {
            const resetToggle = !(options && options.keepWantSelect === true);
            this.activeStudents = [];
            this.selectedStudentIds = [];
            this.studentPickerSearch = '';
            this.studentsLoading = false;
            this.studentPickerLoaded = false;
            this.assignedStudentsLoadFailed = false;
            if (resetToggle) {
                this.wantSelectStudents = false;
                this.hadStaffAssignedOnOpen = false;
            }
        },
        onWantSelectStudentsChange() {
            if (!this.wantSelectStudents) {
                this.activeStudents = [];
                this.selectedStudentIds = [];
                this.studentPickerSearch = '';
                this.studentPickerLoaded = false;
                // Keep hadStaffAssignedOnOpen so save can clear prior assignments.
                return;
            }
            this.fetchActiveStudents();
        },
        /**
         * Suggest a minimum capacity from the selection. Never locks the field —
         * staff can raise it or clear it for unlimited. Does not wipe a higher
         * value the user already entered.
         */
        syncStudentCountFromSelection(allowClear) {
            const selectedCount = this.selectedStudentIds.length;
            if (selectedCount > 0) {
                const currentRaw = this.form.student_count;
                const current = currentRaw === '' || currentRaw === null || typeof currentRaw === 'undefined'
                    ? null
                    : parseInt(currentRaw, 10);
                if (current === null || isNaN(current) || current < selectedCount) {
                    this.form.student_count = String(selectedCount);
                }
                this.clearValidation('student_count');
                return;
            }
            if (allowClear === false) {
                return;
            }
            // Leaving capacity as-is when selection is cleared keeps manual edits.
        },
        isStudentSelected(studentId) {
            const id = studentId ? String(studentId) : '';
            if (!id) return false;
            return this.selectedStudentIds.indexOf(id) !== -1;
        },
        toggleStudentSelection(studentId, event) {
            const id = studentId ? String(studentId) : '';
            if (!id) return;
            const checked = event && event.target ? !!event.target.checked : false;
            const next = this.selectedStudentIds.slice();
            const index = next.indexOf(id);
            if (checked && index === -1) {
                next.push(id);
            } else if (!checked && index !== -1) {
                next.splice(index, 1);
            }
            this.selectedStudentIds = next;
            this.syncStudentCountFromSelection();
        },
        selectAllFilteredStudents() {
            const nextLookup = {};
            const next = this.selectedStudentIds.slice();
            for (let i = 0; i < next.length; i++) {
                nextLookup[String(next[i])] = true;
            }
            for (let j = 0; j < this.filteredActiveStudents.length; j++) {
                const id = this.filteredActiveStudents[j] && this.filteredActiveStudents[j].student_id
                    ? String(this.filteredActiveStudents[j].student_id)
                    : '';
                if (id && !nextLookup[id]) {
                    next.push(id);
                    nextLookup[id] = true;
                }
            }
            this.selectedStudentIds = next;
            this.syncStudentCountFromSelection();
        },
        clearStudentSelection() {
            this.selectedStudentIds = [];
            this.clearValidation('student_count');
        },
        async fetchActiveStudents() {
            if (!this.wantSelectStudents) {
                return;
            }

            const entity = this.form.entity ? String(this.form.entity).trim() : '';
            const campusCodes = this.campusCodesAsStringArray(this.form.campus_code);
            if (!entity || campusCodes.length === 0) {
                this.activeStudents = [];
                this.studentPickerLoaded = false;
                return;
            }

            // Entity/campus edits can overlap; only the newest response may update state.
            this.studentFetchToken += 1;
            const fetchToken = this.studentFetchToken;

            this.studentsLoading = true;
            try {
                const result = await this.$apollo.query({
                    query: gql`
                        query InternshipClinicalPlacementActiveStudents(
                            $entity: String!
                            $campusCodes: [String!]!
                            $opportunityId: ID
                        ) {
                            internshipClinicalPlacementActiveStudents(
                                entity: $entity
                                campus_codes: $campusCodes
                                opportunity_id: $opportunityId
                            ) {
                                error
                                error_code
                                message
                                data {
                                    student_id
                                    user_id
                                    is_registered
                                    name_en
                                    eid
                                    entity
                                    campus_code
                                }
                            }
                        }
                    `,
                    variables: {
                        entity: entity,
                        campusCodes: campusCodes,
                        opportunityId: this.isEditMode && this.editingOpportunityId
                            ? this.editingOpportunityId
                            : null,
                    },
                    fetchPolicy: 'network-only',
                    errorPolicy: 'all',
                });

                if (fetchToken !== this.studentFetchToken) {
                    return;
                }

                const payload = result
                    && result.data
                    && result.data.internshipClinicalPlacementActiveStudents;
                if (!payload || payload.error) {
                    const msg = payload && payload.message
                        ? payload.message
                        : 'Failed to load students.';
                    this.toast('error', msg);
                    this.activeStudents = [];
                    this.studentPickerLoaded = false;
                    return;
                }

                const data = payload.data ? payload.data : [];
                this.activeStudents = Array.isArray(data) ? data : [];

                const validLookup = {};
                for (let i = 0; i < this.activeStudents.length; i++) {
                    const id = this.activeStudents[i] && this.activeStudents[i].student_id
                        ? String(this.activeStudents[i].student_id)
                        : '';
                    if (id) {
                        validLookup[id] = true;
                    }
                }
                this.selectedStudentIds = this.selectedStudentIds.filter(function (id) {
                    return !!validLookup[String(id)];
                });
                this.studentPickerLoaded = true;
                this.syncStudentCountFromSelection(false);
            } catch (e) {
                console.error('Error fetching active students:', e);
                if (fetchToken !== this.studentFetchToken) {
                    return;
                }
                this.activeStudents = [];
                this.studentPickerLoaded = false;
                this.toast('error', 'Failed to load students. Please try again.');
            } finally {
                if (fetchToken === this.studentFetchToken) {
                    this.studentsLoading = false;
                }
            }
        },
        openCreateModal() {
            this.formModalMode = "create";
            this.resetForm();
            this.resetValidations();
            if (!this.form.entity) {
                this.initializeDefaults();
            } else {
                if (!this.selectedEntityOption) {
                    this.selectedEntityOption = { label: this.getEntityName(this.form.entity), value: this.form.entity };
                }
                this.fetchCampusesForEntity(this.form.entity);
            }
        },
        async openEditModal(item) {
            if (!this.canEditOpportunity(item)) {
                return;
            }
            const entity = item && item.entity ? String(item.entity).trim() : "";
            if (!entity) {
                this.toast("error", "This opportunity cannot be edited.");
                return;
            }

            var campusCodes = [];
            if (item && item.campus_code) {
                if (Array.isArray(item.campus_code)) {
                    campusCodes = this.campusCodesAsStringArray(item.campus_code);
                } else {
                    campusCodes = this.campusCodesAsStringArray([item.campus_code]);
                }
            }

            this.formModalMode = "edit";
            this.editingOpportunityId = item && item.id ? item.id : null;
            this.resetValidations();
            this.form = {
                entity: entity,
                opportunity_type: item && item.opportunity_type ? item.opportunity_type : "clinical_placement",
                campus_code: [],
                title: item && item.title ? String(item.title) : "",
                description: item && item.description ? String(item.description) : "",
                student_count: item && item.student_count !== null && typeof item.student_count !== "undefined"
                    ? String(item.student_count)
                    : "",
                start_date: this.normalizeDateForInput(item && item.start_date ? item.start_date : ""),
                end_date: this.normalizeDateForInput(item && item.end_date ? item.end_date : ""),
            };

            this.suppressEntityCampusReset = true;
            this.form.entity = entity;
            this.selectedEntityOption = { label: this.getEntityName(entity), value: entity };
            await this.fetchCampusesForEntity(entity);
            this.form.campus_code = this.mapCampusCodesToOptions(campusCodes);
            this.clearStudentPickerState();
            this.suppressEntityCampusReset = false;

            const assignedStudentIds = await this.fetchAssignedStudentIds(this.editingOpportunityId);
            this.assignedStudentsLoadFailed = assignedStudentIds === null;
            this.selectedStudentIds = assignedStudentIds === null ? [] : assignedStudentIds;
            this.hadStaffAssignedOnOpen = this.selectedStudentIds.length > 0;
            this.wantSelectStudents = this.hadStaffAssignedOnOpen;
            if (this.wantSelectStudents) {
                await this.fetchActiveStudents();
            }
        },
        /**
         * Returns null when the assignments could not be loaded, so a failed lookup is
         * never mistaken for "no students assigned" while saving.
         */
        async fetchAssignedStudentIds(opportunityId) {
            if (!opportunityId) {
                return [];
            }

            try {
                const result = await this.$apollo.query({
                    query: gql`
                        query InternshipClinicalPlacementAssignedStudentIds($id: ID!) {
                            internshipClinicalPlacementAssignedStudentIds(id: $id) {
                                error
                                error_code
                                message
                                data
                            }
                        }
                    `,
                    variables: { id: opportunityId },
                    fetchPolicy: 'network-only',
                    errorPolicy: 'all',
                });

                if (result && result.errors && result.errors.length > 0) {
                    console.error('Assigned students GraphQL errors:', result.errors);
                    this.toast('error', 'Could not load the students already assigned to this opportunity.');
                    return null;
                }

                const payload = result
                    && result.data
                    && result.data.internshipClinicalPlacementAssignedStudentIds;
                if (!payload || payload.error) {
                    const msg = payload && payload.message
                        ? payload.message
                        : 'Could not load the students already assigned to this opportunity.';
                    this.toast('error', msg);
                    return null;
                }

                const data = payload.data ? payload.data : [];
                if (!Array.isArray(data)) {
                    return [];
                }
                return data.map(function (id) {
                    return String(id);
                });
            } catch (e) {
                console.error('Error fetching assigned students:', e);
                this.toast('error', 'Could not load the students already assigned to this opportunity.');
                return null;
            }
        },
        closeFormModal() {
            this.formModalMode = "";
            this.resetForm();
            this.resetValidations();
        },
        resetValidations() {
            this.validations = {
                entity: '',
                opportunity_type: '',
                campus_code: '',
                title: '',
                student_count: '',
                start_date: '',
                end_date: '',
            };
        },
        clearValidation(key) {
            if (this.validations && this.validations[key]) {
                this.validations[key] = '';
            }
        },
        onEntitySelected(option) {
            // v-model will update; keep this for clarity + compatibility.
            this.selectedEntityOption = option || null;
        },
        onEntityRemoved() {
            this.selectedEntityOption = null;
            this.form.entity = '';
            this.form.campus_code = [];
            this.campusOptions = [];
            this.clearStudentPickerState();
        },
        formatCampuses(value) {
            if (!value) return '';
            if (Array.isArray(value)) {
                return value.join(', ');
            }
            return String(value);
        },
        formatEnrolledCount(item) {
            if (!item) {
                return 0;
            }
            var count = item.enrolled_count;
            if (count === null || typeof count === 'undefined') {
                return 0;
            }
            return count;
        },
        enrollmentStatusCount(item, status) {
            if (!item) {
                return 0;
            }
            var count = 0;
            if (status === 'pending') {
                count = item.pending_enrollment_count;
            } else if (status === 'approved') {
                count = item.approved_enrollment_count;
            } else if (status === 'rejected') {
                count = item.rejected_enrollment_count;
            }
            if (count === null || typeof count === 'undefined') {
                return 0;
            }
            return count;
        },
        enrollmentBreakdownTitle(item) {
            return 'Pending: ' + this.enrollmentStatusCount(item, 'pending')
                + ' · Approved: ' + this.enrollmentStatusCount(item, 'approved')
                + ' · Rejected: ' + this.enrollmentStatusCount(item, 'rejected');
        },
        showEnrollmentTooltip(event, item) {
            const target = event && event.currentTarget ? event.currentTarget : null;
            if (!target) {
                return;
            }

            const rect = target.getBoundingClientRect();
            const tooltipHalfWidth = 95;
            const viewportPadding = 12;
            const left = Math.max(
                tooltipHalfWidth + viewportPadding,
                Math.min(rect.left + (rect.width / 2), window.innerWidth - tooltipHalfWidth - viewportPadding)
            );
            const showAbove = window.innerHeight - rect.bottom < 150;

            this.enrollmentTooltip = {
                visible: true,
                left: left,
                top: showAbove ? rect.top - 10 : rect.bottom + 10,
                placement: showAbove ? 'top' : 'bottom',
                pending: this.enrollmentStatusCount(item, 'pending'),
                approved: this.enrollmentStatusCount(item, 'approved'),
                rejected: this.enrollmentStatusCount(item, 'rejected'),
            };
        },
        hideEnrollmentTooltip() {
            this.enrollmentTooltip.visible = false;
        },
        async fetchCampusesForEntity(entity) {
            const safeEntity = entity ? String(entity).trim() : '';
            if (!safeEntity) return;
            this.campusesLoading = true;
            try {
                const result = await this.$apollo.query({
                    query: gql`
                        query GetMyCampusesForEntity($entities: [String!]!) {
                            getMyCampusesForEntities(entities: $entities) {
                                error
                                message
                                data {
                                    value
                                    label
                                }
                            }
                        }
                    `,
                    variables: { entities: [safeEntity] },
                    fetchPolicy: 'network-only',
                    errorPolicy: 'all',
                });

                const payload = result && result.data && result.data.getMyCampusesForEntities;
                if (!payload || payload.error) {
                    if (result && result.errors && result.errors.length > 0) {
                        const first = result.errors[0];
                        const graphMsg = first && first.message ? first.message : 'GraphQL error';
                        console.error('getMyCampusesForEntities GraphQL errors:', result.errors);
                        this.toast('error', graphMsg);
                    } else {
                        const msg = payload && payload.message ? payload.message : 'Failed to load campuses.';
                        this.toast('error', msg);
                    }
                    this.campusOptions = [];
                    return;
                }
                const data = payload && payload.data ? payload.data : [];
                this.campusOptions = Array.isArray(data) ? data : [];

                if (this.campusOptions.length === 1 && this.formModalMode !== "edit") {
                    this.form.campus_code = [this.campusOptions[0]];
                }
            } catch (e) {
                console.error('Error fetching campuses:', e);
                this.campusOptions = [];
            } finally {
                this.campusesLoading = false;
            }
        },
        /**
         * GraphQL expects campus_code: [String!]. Multiselect / Apollo options may be
         * { value, label, __typename } objects; coerce each entry to its code string.
         */
        campusCodesAsStringArray(codes) {
            var out = [];
            var list = codes || [];
            for (var i = 0; i < list.length; i++) {
                var c = list[i];
                if (typeof c === 'string') {
                    out.push(c);
                } else if (c && typeof c.value === 'string') {
                    out.push(c.value);
                }
            }
            return out;
        },
        mapCampusCodesToOptions(codes) {
            var codeList = this.campusCodesAsStringArray(codes);
            var selected = [];
            var options = this.campusOptions || [];
            for (var i = 0; i < codeList.length; i++) {
                var code = codeList[i];
                var found = null;
                for (var j = 0; j < options.length; j++) {
                    var opt = options[j];
                    if (opt && String(opt.value) === String(code)) {
                        found = opt;
                        break;
                    }
                }
                if (found) {
                    selected.push(found);
                } else if (code) {
                    selected.push({ label: code, value: code });
                }
            }
            return selected;
        },
        validateForm() {
            var ok = true;
            this.resetValidations();

            if (!this.form.entity) {
                this.validations.entity = 'Entity is required.';
                ok = false;
            }
            if (this.form.opportunity_type !== 'clinical_placement' && this.form.opportunity_type !== 'internship') {
                this.validations.opportunity_type = 'Select clinical placement or internship.';
                ok = false;
            }
            if (!this.form.campus_code || this.form.campus_code.length === 0) {
                this.validations.campus_code = 'At least one campus is required.';
                ok = false;
            }
            if (!this.form.title || !this.form.title.trim()) {
                this.validations.title = 'Title is required.';
                ok = false;
            }

            // Student count: blank or > 0 (no 0). If students are selected, count must cover them.
            if (this.form.student_count !== '' && this.form.student_count !== null && typeof this.form.student_count !== 'undefined') {
                const n = parseInt(this.form.student_count, 10);
                if (isNaN(n) || n <= 0) {
                    this.validations.student_count = 'Student count must be blank or greater than 0.';
                    ok = false;
                } else if (this.selectedStudentIds.length > 0 && n < this.selectedStudentIds.length) {
                    this.validations.student_count = 'Student count cannot be less than the number of selected students ('
                        + this.selectedStudentIds.length + ').';
                    ok = false;
                }
            }

            if (!this.form.start_date) {
                this.validations.start_date = 'Listing open date is required.';
                ok = false;
            }
            if (!this.form.end_date) {
                this.validations.end_date = 'Listing close date is required.';
                ok = false;
            }

            if (this.form.start_date && this.form.end_date) {
                if (this.form.end_date < this.form.start_date) {
                    this.validations.end_date = 'Listing close date must be on or after the open date.';
                    ok = false;
                }
            }

            return ok;
        },
        buildFormInput() {
            const input = {
                entity: this.form.entity,
                opportunity_type: this.form.opportunity_type,
                campus_code: this.campusCodesAsStringArray(this.form.campus_code),
                title: this.form.title,
                description: this.form.description || null,
                student_count: this.form.student_count === '' ? null : parseInt(this.form.student_count, 10),
                start_date: this.form.start_date,
                end_date: this.form.end_date,
            };

            if (this.isEditMode && this.assignedStudentsLoadFailed) {
                return input;
            }

            if (this.wantSelectStudents) {
                input.selected_student_ids = this.selectedStudentIds.slice();
            } else if (this.isEditMode && this.hadStaffAssignedOnOpen) {
                // Staff turned selection off: remove prior staff assignments.
                input.selected_student_ids = [];
            }

            return input;
        },
        async submitForm() {
            if (this.isEditMode) {
                await this.submitUpdate();
            } else {
                await this.submitCreate();
            }
        },
        async submitCreate() {
            if (!this.hasPermission('internship-create')) {
                this.$router.replace('/no-permission');
                return;
            }

            if (!this.validateForm()) return;

            this.isSubmitting = true;
            try {
                const mutation = gql`
                    mutation CreateInternshipClinicalPlacementOpportunity(
                        $input: CreateInternshipClinicalPlacementOpportunityInput!
                    ) {
                        createInternshipClinicalPlacementOpportunity(input: $input) {
                            error
                            error_code
                            message
                            data {
                                id
                                token
                            }
                        }
                    }
                `;

                const result = await this.$apollo.mutate({
                    mutation,
                    variables: { input: this.buildFormInput() },
                });

                const payload = result && result.data && result.data.createInternshipClinicalPlacementOpportunity;
                if (!payload || payload.error) {
                    const msg = payload && payload.message ? payload.message : 'Failed to create.';
                    this.toast("error", msg);
                    return;
                }

                this.toast("success", payload.message || 'Created successfully.');
                this.closeFormModal();
                this.fetchOpportunities();
            } catch (e) {
                console.error('Create failed:', e);
                this.toast("error", 'Failed to create. Please try again.');
            } finally {
                this.isSubmitting = false;
            }
        },
        async submitUpdate() {
            if (!this.hasPermission('internship-edit')) {
                this.$router.replace('/no-permission');
                return;
            }

            if (!this.editingOpportunityId) {
                this.toast("error", "No opportunity selected to update.");
                return;
            }

            if (!this.validateForm()) return;

            this.isSubmitting = true;
            try {
                const mutation = gql`
                    mutation UpdateInternshipClinicalPlacementOpportunity(
                        $id: ID!
                        $input: UpdateInternshipClinicalPlacementOpportunityInput!
                    ) {
                        updateInternshipClinicalPlacementOpportunity(id: $id, input: $input) {
                            error
                            error_code
                            message
                            data {
                                id
                                token
                            }
                        }
                    }
                `;

                const result = await this.$apollo.mutate({
                    mutation,
                    variables: {
                        id: String(this.editingOpportunityId),
                        input: this.buildFormInput(),
                    },
                });

                const payload = result && result.data && result.data.updateInternshipClinicalPlacementOpportunity;
                if (!payload || payload.error) {
                    const msg = payload && payload.message ? payload.message : 'Failed to update.';
                    this.toast("error", msg);
                    return;
                }

                this.toast("success", payload.message || 'Updated successfully.');
                this.closeFormModal();
                this.fetchOpportunities();
            } catch (e) {
                console.error('Update failed:', e);
                this.toast("error", 'Failed to update. Please try again.');
            } finally {
                this.isSubmitting = false;
            }
        },
    }
}
</script>

<style scoped>
.icp-page {
    position: relative;
    overflow: hidden;
}

.icp-page-bg {
    position: absolute;
    inset: 0;
    z-index: 0;
    background:
        radial-gradient(ellipse 80% 50% at 10% -10%, rgba(37, 99, 235, 0.12), transparent 55%),
        radial-gradient(ellipse 60% 40% at 90% 10%, rgba(14, 116, 144, 0.1), transparent 50%),
        linear-gradient(165deg, #f1f5f9 0%, #e8eef6 45%, #f0f7fa 100%);
}

.icp-page-bg::after {
    content: "";
    position: absolute;
    inset: 0;
    opacity: 0.35;
    background-image:
        linear-gradient(rgba(15, 23, 42, 0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(15, 23, 42, 0.03) 1px, transparent 1px);
    background-size: 48px 48px;
    pointer-events: none;
}

.icp-breadcrumb-link {
    color: #64748b;
    font-weight: 500;
    transition: color 0.2s ease;
}

.icp-breadcrumb-link:hover {
    color: #1e3a8a;
}

.icp-breadcrumb-sep {
    color: #94a3b8;
}

.icp-breadcrumb-current {
    color: #0f172a;
    font-weight: 600;
}

.icp-hero-kicker {
    margin: 0 0 0.5rem;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #0369a1;
}

.icp-hero-title {
    margin: 0 0 0.75rem;
    font-family: Georgia, "Times New Roman", serif;
    font-size: 2.25rem;
    line-height: 1.15;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
}

.icp-hero-lead {
    margin: 0;
    max-width: 42rem;
    font-size: 1.0625rem;
    line-height: 1.65;
    color: #475569;
}

@media (min-width: 768px) {
    .icp-hero-title {
        font-size: 2.75rem;
    }
}

@media (max-width: 639px) {
    .icp-hero-title {
        font-size: 1.875rem;
    }
}

/* Primary action button — mirrors document-control Add Document (dc-add-btn) */
.icl-add-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    white-space: nowrap;
    border-radius: 0.75rem;
    border: 0;
    background: linear-gradient(180deg, #1e3a8a 0%, #2563eb 100%);
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #ffffff;
    box-shadow: 0 8px 20px -10px rgba(37, 99, 235, 0.55);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.icl-add-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 12px 24px -10px rgba(37, 99, 235, 0.65);
}

.icl-add-btn:active {
    transform: translateY(0);
}

.icl-add-btn:disabled {
    pointer-events: none;
    opacity: 0.6;
}

.icl-scope-tabs {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: #f8fafc;
}

.icl-scope-tab {
    min-height: 36px;
    padding: 8px 14px;
    border: 0;
    border-radius: 9px;
    color: #64748b;
    background: transparent;
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;
    transition: color 150ms ease, background 150ms ease, box-shadow 150ms ease;
}

.icl-scope-tab:hover {
    color: #1e40af;
    background: #eff6ff;
}

.icl-scope-tab-active {
    color: #1d4ed8;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08), 0 4px 12px rgba(37, 99, 235, 0.08);
}

.icl-add-btn__icon {
    width: 1rem;
    height: 1rem;
    display: inline-block;
}

.icl-title-link {
    color: #111827;
    text-decoration: none;
    cursor: pointer;
    transition: color 150ms ease, text-decoration-color 150ms ease;
}

.icl-title-link:hover {
    color: #1d4ed8;
    text-decoration: underline;
}

.icl-status-chip {
    display: inline-flex;
    align-items: center;
    border-radius: 9999px;
    font-size: 11px;
    font-weight: 600;
    padding: 5px 11px;
    border: 1px solid transparent;
}

.icl-status-chip-active {
    background: #ecfdf5;
    border-color: #6ee7b7;
    color: #047857;
}

.icl-enrollment-summary {
    position: relative;
    display: inline-flex;
    align-items: center;
}

.icl-enrollment-total {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-width: 78px;
    padding: 7px 10px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    background: #f8fafc;
    cursor: help;
    transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease;
}

.icl-enrollment-total:hover,
.icl-enrollment-total:focus {
    outline: none;
    border-color: #bfdbfe;
    background: #eff6ff;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.icl-enrollment-total-value {
    color: #0f172a;
    font-size: 15px;
    font-weight: 800;
    letter-spacing: -0.02em;
    line-height: 1;
}

.icl-enrollment-info-icon {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    color: #94a3b8;
}

.icl-enrollment-tooltip {
    position: fixed;
    z-index: 9999;
    width: 190px;
    padding: 12px;
    border: 1px solid #1e293b;
    border-radius: 12px;
    color: #e2e8f0;
    background: #0f172a;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.28);
    pointer-events: none;
    transform: translateX(-50%);
}

.icl-enrollment-tooltip::after {
    position: absolute;
    left: 50%;
    width: 0;
    height: 0;
    border: 6px solid transparent;
    content: "";
    transform: translateX(-50%);
}

.icl-enrollment-tooltip-bottom::after {
    bottom: 100%;
    border-bottom-color: #0f172a;
}

.icl-enrollment-tooltip-top {
    transform: translate(-50%, -100%);
}

.icl-enrollment-tooltip-top::after {
    top: 100%;
    border-top-color: #0f172a;
}

.icl-enrollment-tooltip-title {
    margin-bottom: 8px;
    color: #ffffff;
    font-size: 11px;
    font-weight: 800;
}

.icl-enrollment-tooltip-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 4px 0;
    font-size: 11px;
}

.icl-enrollment-tooltip-row strong {
    color: #ffffff;
    font-size: 12px;
}

.icl-enrollment-tooltip-label {
    display: inline-flex;
    align-items: center;
    gap: 7px;
}

.icl-enrollment-tooltip-dot {
    width: 6px;
    height: 6px;
    border-radius: 9999px;
    flex-shrink: 0;
}

.icl-enrollment-tooltip-dot-pending {
    background: #f59e0b;
}

.icl-enrollment-tooltip-dot-approved {
    background: #10b981;
}

.icl-enrollment-tooltip-dot-rejected {
    background: #ef4444;
}

.icl-status-chip-inactive {
    background: #f3f4f6;
    border-color: #e5e7eb;
    color: #6b7280;
}

.ftv-input {
    width: 100%;
    padding: 0.5rem 1rem;
    border: 1px solid #d1d5db;
    border-radius: 0.75rem;
    background: #ffffff;
}

.ftv-input:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.35);
    border-color: rgba(59, 130, 246, 0.7);
}

.ftv-input-invalid {
    border-color: #fca5a5;
}

.ftv-textarea {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #d1d5db;
    border-radius: 0.75rem;
    background: #ffffff;
}

.ftv-textarea:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.35);
    border-color: rgba(59, 130, 246, 0.7);
}

/* vue-multiselect theming to match inputs */
:deep(.multiselect) {
    min-height: 42px;
}

:deep(.multiselect__tags) {
    border: 1px solid #d1d5db;
    border-radius: 0.75rem;
    padding: 8px 40px 8px 12px;
    min-height: 42px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
}

:deep(.multiselect__tags-wrap) {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    vertical-align: middle;
}

:deep(.multiselect__input),
:deep(.multiselect__single) {
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 0;
    vertical-align: middle;
    align-self: center;
}

:deep(.multiselect__single) {
    padding-left: 0;
    margin-bottom: 0;
}

:deep(.multiselect__placeholder) {
    color: #9ca3af;
    margin-bottom: 0;
    padding-top: 0;
    line-height: 20px;
}

:deep(.multiselect__option--highlight) {
    background: #2563eb;
}

:deep(.multiselect__option--selected.multiselect__option--highlight) {
    background: #1d4ed8;
}

:deep(.multiselect__tag) {
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
}

:deep(.multiselect__tag-icon:after) {
    color: #1d4ed8;
}

.multiselect-invalid :deep(.multiselect__tags) {
    border-color: #fca5a5;
}

.icl-student-picker {
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.icl-student-picker-list {
    max-height: 18rem;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
}

.icl-student-picker-item + .icl-student-picker-item {
    border-top: 1px solid #f1f5f9;
}

.icl-student-picker-label:hover {
    background: #f8fafc;
}

.icl-student-picker-checkbox {
    width: 1rem;
    height: 1rem;
    accent-color: #2563eb;
    cursor: pointer;
}

.icl-student-reg-pill {
    display: inline-flex;
    align-items: center;
    padding: 1px 8px;
    border-radius: 9999px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
}

.icl-student-reg-pill--yes {
    background: #ecfdf5;
    color: #047857;
    border: 1px solid #a7f3d0;
}

.icl-student-reg-pill--no {
    background: #fff7ed;
    color: #c2410c;
    border: 1px solid #fed7aa;
}

.ftv-input:disabled {
    background: #f8fafc;
    color: #64748b;
    cursor: not-allowed;
}
</style>
